import type { Tag } from '@finapp/types';
import type React from 'react';
import { useTagAutocomplete } from '../hooks/useTagAutocomplete';

interface SingleTagSelectorProps {
  selectedTag: Tag | null;
  onTagChange: (tag: Tag | null) => void;
  placeholder?: string;
  'data-testid'?: string;
}

export const SingleTagSelector: React.FC<SingleTagSelectorProps> = ({
  selectedTag,
  onTagChange,
  placeholder = 'Select a tag...',
  'data-testid': dataTestId,
}) => {
  const {
    inputValue,
    setInputValue,
    suggestions,
    isLoading,
    showSuggestions,
    setShowSuggestions,
    handleTagSelect,
    handleInputKeyDown,
    handleAddCurrentInput,
  } = useTagAutocomplete({
    onTagResolved: (tag) => {
      onTagChange(tag);
    },
  });

  const handleTagRemove = () => {
    onTagChange(null);
  };

  return (
    <div
      className="relative"
      data-testid={dataTestId ? `${dataTestId}-container` : 'single-tag-selector-container'}
    >
      {selectedTag ? (
        <div className="flex items-center gap-2 mb-2">
          <span
            className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
            data-testid={`tag-${selectedTag.display_name}`}
          >
            {selectedTag.display_name}
            <button
              onClick={handleTagRemove}
              className="ml-2 text-blue-600 hover:text-blue-800 font-bold"
              data-testid={`tag-${selectedTag.display_name}-remove`}
            >
              ×
            </button>
          </span>
        </div>
      ) : (
        <div className="relative">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onFocus={() => setShowSuggestions(inputValue.length > 0)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              data-testid={dataTestId ? `${dataTestId}-input` : 'single-tag-selector-input'}
            />
            {inputValue.trim() && (
              <button
                type="button"
                onClick={handleAddCurrentInput}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                title={`Add "${inputValue.trim()}" as a new tag`}
              >
                Add
              </button>
            )}
          </div>
          {!selectedTag && !inputValue && (
            <p className="text-xs text-gray-500 mt-1">
              Type to search existing tags or create a new one. Press Enter or click Add.
            </p>
          )}

          {isLoading && (
            <div className="absolute right-3 top-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}

          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto">
              {suggestions.map((option, index) => (
                <button
                  key={`${option.value}-${index}`}
                  onClick={() => handleTagSelect(option)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{option.label}</span>
                    <div className="flex items-center space-x-2">
                      {option.is_new ? (
                        <span className="inline-flex px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                          Create new
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                          Use existing
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
